"use client";

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["icon", "variant", "label", "loading"];
import React from "react";
import ChevronIcon from "../../icons/dist/ChevronIcon";
import styled, { css } from "styled-components";
import VisuallyHidden from "../utils/VisuallyHidden";
import { minTapSize } from "../../dimensions";
import { buttonreset } from "../../utils/snippets";
import { dark, darker, muted, transparent } from "../../colors";
import { getButtonType } from "../../utils/getButtonType";
import LoadingSpinner from "../LoadingSpinner";
var BaseSquareButton = function BaseSquareButton(props) {
  var icon = props.icon,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? "primary" : _props$variant,
    label = props.label,
    loading = props.loading,
    rest = _objectWithoutProperties(props, _excluded);

  // Conditionally set href to undefined when [aria-disabled="true"]
  var href = props["aria-disabled"] === "true" ? undefined : props.href;
  var showLoadingState = (loading === null || loading === void 0 ? void 0 : loading.isLoading) && !(props.disabled || props["aria-disabled"] === "true");
  var getLoadingColorVariant = function getLoadingColorVariant() {
    if (variant === "secondary" || variant === "ghost") {
      return "primary";
    }
    if (variant === "tertiary") {
      return "secondary";
    }
    return "inverse";
  };
  return /*#__PURE__*/React.createElement(StyledSquareButton, _extends({
    $variant: variant,
    $showLoadingState: showLoadingState
  }, rest, {
    // Conditionally add type only when the component is used as a button
    type: getButtonType(props),
    href: href
  }), /*#__PURE__*/React.createElement(IconWrapper, null, showLoadingState ? /*#__PURE__*/React.createElement(LoadingSpinner, {
    colorVariant: getLoadingColorVariant()
  }) : icon || /*#__PURE__*/React.createElement(ChevronIcon, {
    rotate: -90,
    size: "2em"
  })), !showLoadingState && /*#__PURE__*/React.createElement(VisuallyHidden, null, label), loading && /*#__PURE__*/React.createElement(VisuallyHidden, {
    "aria-live": "assertive",
    role: "status"
  }, showLoadingState && loading.hiddenLoadingMessage));
};
var SquareButton = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return (
    /*#__PURE__*/
    // @ts-expect-error - I'm not sure how to fix the ref type
    React.createElement(BaseSquareButton, _extends({}, props, {
      ref: ref
    }))
  );
});
export default SquareButton;
var variantStyles = function variantStyles(_ref) {
  var $variant = _ref.$variant,
    theme = _ref.theme;
  switch ($variant) {
    case "primary":
      return css(["color:", ";background-color:", ";"], theme._buttons.primary.color, theme._buttons.primary.backgroundColor);
    case "secondary":
      return css(["border:solid;border-color:", ";color:", ";background-color:transparent;"], theme._buttons.primary.backgroundColor, theme._buttons.primary.backgroundColor);
    case "tertiary":
      return css(["border:solid;border-color:", ";color:", ";background-color:transparent;"], theme._buttons.tertiary.backgroundColor, theme._buttons.tertiary.backgroundColor);
    case "ghost":
      return css(["border-color:transparent;color:", ";background-color:transparent;"], theme._buttons.primary.backgroundColor);
    case "inverse":
      return css(["border:solid;border-color:", ";color:", ";background-color:transparent;"], theme._buttons.inverse.backgroundColor, theme._buttons.inverse.backgroundColor);
  }
};
var hoverStyles = function hoverStyles(_ref2) {
  var $variant = _ref2.$variant,
    theme = _ref2.theme;
  switch ($variant) {
    case "primary":
      return css(["background-color:", ";"], dark(theme._buttons.primary.backgroundColor));
    case "secondary":
      return css(["color:", ";background-color:", ";"], dark(theme._buttons.primary.backgroundColor), muted(theme._buttons.primary.backgroundColor));
    case "tertiary":
      return css(["color:", ";background-color:", ";"], theme._buttons.tertiary.color, theme._buttons.tertiary.backgroundColor);
    case "ghost":
      return css(["color:", ";background-color:", ";"], dark(theme._buttons.primary.backgroundColor), muted(theme._buttons.primary.backgroundColor));
    case "inverse":
      return css(["background-color:", ";"], transparent(theme._buttons.inverse.backgroundColor));
  }
};
var disabledStyles = function disabledStyles(props) {
  switch (props.$variant) {
    case "inverse":
      return css(["border-color:", ";background-color:transparent;opacity:0.3;"], props.theme._buttons.inverse.backgroundColor);
    case "secondary":
      return css(["border-color:", ";color:", ";background-color:transparent;"], props.theme._buttons.disabled.color, props.theme._buttons.disabled.color);
    case "tertiary":
      return css(["border-color:", ";color:", ";background-color:transparent;"], props.theme._buttons.disabled.color, props.theme._buttons.disabled.color);
    case "ghost":
      return css(["color:", ";background-color:transparent;"], props.theme._buttons.disabled.color);
    default:
      return css(["border-color:", ";color:", ";background-color:", ";"], props.theme._buttons.disabled.backgroundColor, props.theme._buttons.disabled.color, props.theme._buttons.disabled.backgroundColor);
  }
};
var activeStyles = css(["border-color:", ";color:", ";background-color:", ";"], function (props) {
  return darker(props.theme.btn.primary);
}, function (props) {
  return props.theme._buttons.primary.color;
}, function (props) {
  return darker(props.theme.btn.primary);
});
var StyledSquareButton = styled.button.withConfig({
  displayName: "SquareButton__StyledSquareButton",
  componentId: "sc-1njhw9f-0"
})(["", ";", ";display:inline-block;width:", ";height:", ";border-width:1px;border-radius:4px;text-align:center;text-decoration:none;transition:background-color 0.2s;&:disabled{", " cursor:not-allowed;}&:not(:disabled){cursor:", ";}&:focus{outline-offset:4px;}&:not(:disabled):hover{", ";}&:not(:disabled):active{", "}&[aria-disabled=\"true\"]{", ";cursor:not-allowed;&:hover,&:focus{", ";}}"], buttonreset, variantStyles, minTapSize, minTapSize, disabledStyles, function (props) {
  return props.$showLoadingState ? "wait" : "pointer";
}, function (props) {
  return !props.$showLoadingState && hoverStyles;
}, function (props) {
  return !props.$showLoadingState && activeStyles;
}, disabledStyles, disabledStyles);
var IconWrapper = styled.div.withConfig({
  displayName: "SquareButton__IconWrapper",
  componentId: "sc-1njhw9f-1"
})(["display:flex;align-items:center;justify-content:center;min-height:100%;"]);